import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Settings, Cog } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const EquipmentRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/equipment-repair"]?.title} description={seoData["services/equipment-repair"]?.description} path="/services/equipment-repair" keywords={seoData["services/equipment-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Equipment Repair & Pump Restoration"
        description="Industrial equipment repair contractor serving California. Pump restoration, bearing housing rebuild, impeller repair, shaft reconditioning, and rotating equipment services using Belzona epoxy repair systems. In-situ repairs — no removal, no hot work, no downtime."
        backgroundKey="equipment-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Bearing housing wear and loss of interference fit',
              'Pump impeller erosion and cavitation damage',
              'Shaft scoring, corrosion, and keyway damage',
              'Valve body erosion and seat wear',
              'Heat exchanger tube sheet corrosion and tube-to-tubesheet leaks',
              'Motor frame corrosion and mounting surface damage'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg">
                <Settings className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Pump & Impeller Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Complete pump rebuild including bearing housing restoration, impeller repair, wear ring replacement, and volute casing repair using Belzona 1111 and 1212 metal repair composites. Return pumps to OEM tolerances without machining.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Bearing Housing Rebuild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Precision bearing housing repair using Belzona 1321 and alignment techniques to restore interference fit on worn housings. Eliminate bearing spin and premature failure without replacement or machining.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Shaft & Keyway Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Shaft scoring repair, keyway rebuild, and journal restoration using epoxy metal composites. Protect repaired surfaces with Belzona 1341 for long-term wear resistance.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Valve & Heat Exchanger Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Valve body rebuild, seat repair, and gate/globe valve restoration. Tube sheet coating and tube-to-tubesheet seal repair for shell and tube heat exchangers using immersion-rated epoxy systems.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['In-situ repairs — fix equipment on-site without removal, transport, or crane costs',
                  'No hot work — cold-applied systems eliminate fire permits and safety shutdowns',
                  'Return equipment to OEM specifications without replacement',
                  'Extend equipment service life by 10-15 years',
                  'Reduce repair costs 50-70% vs. equipment replacement',
                  'Manufacturer-backed warranties on all installed repair systems',
                  '24/7 emergency response for critical equipment failures',
                  'AMPP/NACE certified applicators with 25+ years experience'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">General purpose metal repair and rebuilding</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1212 (Fluidmetal)</p>
                  <p className="text-xs text-slate-500 mt-1">Pourable compound for large volume rebuilds</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Erosion-resistant repair for pumps and valves</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1341 (Supermetalglide)</p>
                  <p className="text-xs text-slate-500 mt-1">Low-friction coating for shafts and bearings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Equipment Down? We Fix It On-Site.</h2>
          <p className="text-blue-100 mb-8">Contact our equipment repair specialists for a free assessment. Emergency response available 24/7 for critical pump, valve, and equipment failures.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EquipmentRepair;
